import React, { memo, useEffect, useRef, useState } from 'react';
import PostCard from './PostCard';
import styled from 'styled-components';
import { posts as defaultPosts } from '../data/posts';
import Footer from './Footer';
import { Link } from 'react-router-dom';
import Newsletter from './Newsletter';
import { Helmet } from 'react-helmet-async';

// Componente Grid optimizado con memo
const Grid = styled.section`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: clamp(1.5rem, 2.5vw, 2.5rem);
  padding-block: 2.5rem;
  margin: 0 auto;
  width: min(90%, 70rem);

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const DonationSection = styled.section`
  background: linear-gradient(145deg, #0f172a 0%, #1e1b4b 100%);
  padding: 80px 20px;
  color: white;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at top right, rgba(30, 64, 175, 0.15), transparent 50%),
                radial-gradient(circle at bottom left, rgba(30, 58, 138, 0.1), transparent 50%);
    pointer-events: none;
  }

  @media (max-width: 768px) {
    padding: 60px 15px;
  }
`;

const DonationContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  position: relative;
  z-index: 2;
`;

const DonationTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  
  span {
    color: #e63946;
  }
  
  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const DonationDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  margin-bottom: 2.5rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const DonationButton = styled(Link)`
  background: linear-gradient(45deg, #e63946 0%, #e63946dd 100%);
  color: white;
  padding: 16px 32px;
  border-radius: 50px;
  text-decoration: none;
  font-size: 1.1rem;
  font-weight: 600;
  display: inline-block;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(230, 57, 70, 0.3);
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
    padding: 14px 28px;
  }
`;

// Hook personalizado para manejo de intersección (lazy loading)
const useIntersectionObserver = (options = {}) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      setIsIntersecting(entry.isIntersecting);
    }, options);

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [options]);

  return [ref, isIntersecting];
};

// Componente de imagen con lazy loading
const LazyImage = memo(({ src, alt, ...props }) => {
  const [ref, isInView] = useIntersectionObserver({
    rootMargin: '200px 0px',
    threshold: 0.1
  });
  const [isLoaded, setIsLoaded] = useState(false);

  return (
    <div ref={ref} style={{ overflow: 'hidden', height: '100%' }}>
      {isInView && (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          onLoad={() => setIsLoaded(true)}
          style={{
            opacity: isLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            ...props.style
          }}
          {...props}
        />
      )}
    </div>
  );
});

// Sección de promoción de la app (reutilizada desde Home/PostPage)
const AppPromoSection = styled.section`
  background: linear-gradient(145deg, #0f172a 0%, #1e1b4b 100%);
  padding: 80px 20px;
  color: white;
  position: relative;
  overflow: hidden;
  height: 50vh; /* Limitar altura al 50% del viewport */
  min-height: 400px; /* Altura mínima para evitar que sea demasiado pequeño */
  display: flex;
  align-items: center;
  &::before {
    content: '';
    position: absolute;
    top: 0;left: 0;right: 0;bottom: 0;
    background: radial-gradient(circle at top right, rgba(30, 64, 175, 0.15), transparent 50%),
                radial-gradient(circle at bottom left, rgba(30, 58, 138, 0.1), transparent 50%);
    pointer-events: none;
  }
  @media (max-width: 768px) {
    padding: 40px 15px;
    height: auto; /* En móviles, permitir altura automática */
    min-height: 50vh; /* En móviles, altura mínima del 50% del viewport */
  }
`;

const AppPromoContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
  @media (max-width: 968px){grid-template-columns: 1fr;text-align:center;}
`;

const AppPromoContent = styled.div`z-index:2;`;

const AppPromoTitle = styled.h2`
  font-size: 2.5rem;font-weight:700;margin-bottom:24px;
  span{color:#e63946;}
  @media(max-width:768px){font-size:2rem;}
  @media(max-width:480px){font-size:1.8rem;}
`;

const AppPromoDescription = styled.p`
  font-size:1.1rem;line-height:1.8;margin-bottom:32px;color:#e2e8f0;
  @media(max-width:480px){font-size:1rem;margin-bottom:24px;}
`;

const AppStoreButton = styled.a`
  display:inline-flex;align-items:center;background:black;color:white;
  padding:12px 24px;border-radius:12px;text-decoration:none;font-weight:600;
  transition:all .3s ease;
  &:hover{transform:translateY(-2px);box-shadow:0 4px 12px rgba(0,0,0,0.2);}
  svg{margin-right:8px;}
  @media(max-width:480px){padding:10px 20px;font-size:.9rem;}
`;

const ScreenshotsContainer = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  z-index: 2;
  width: 70%;
  height: 90%;
  margin: 0 auto;
  
  @media (max-width: 968px) {
    height: 350px;
    max-width: 600px;
  }
  
  @media (max-width: 768px) {
    height: 300px;
    max-width: 500px;
  }
  
  @media (max-width: 480px) {
    height: auto;
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const ScreenshotContainer = styled.div`
  width: 65%;
  height: 90%;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0,0,0,0.2);
  transition: all 0.3s ease;
  margin: auto;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 12px 30px rgba(0,0,0,0.3);
  }
  
  @media (max-width: 480px) {
    height: 400px;
  }
`;

const Screenshot = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
`;

// Componente HeroCard para destacar el post principal
const HeroCard = styled.div`
  width: min(80%, 70rem);
  margin: 3rem auto 1rem;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.08);
  border-radius: 16px;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(15, 23, 42, 0.12);
  }
  
  /* El PostCard dentro del HeroCard debería tener un estilo especial */
  & > div {
    margin: 0;
    height: 100%;
  }
  
  /* Responsive adjustments */
  @media (max-width: 768px) {
   width: min(70%, 70rem);
    margin-top: 2rem;
    border-radius: 12px;
  }
`;

// Componente AppPromo separado y memorizado
const AppPromo = memo(() => (
  <AppPromoSection>
    <AppPromoContainer>
      <AppPromoContent>
        <AppPromoTitle>
          Tu entrenador personal en la <span>palma de tu mano</span>
        </AppPromoTitle>
        <AppPromoDescription>
          Descarga nuestra app y lleva tu entrenamiento al siguiente nivel.
          Análisis en tiempo real, seguimiento de progreso y rutinas personalizadas,
          todo en una sola aplicación.
        </AppPromoDescription>
        <AppStoreButton href="https://apps.apple.com/es/app/mypunch/id6477765141?platform=iphone" target="_blank" rel="noopener noreferrer">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
          Disponible en App Store
        </AppStoreButton>
      </AppPromoContent>
      <ScreenshotsContainer>
        <ScreenshotContainer>
          <Screenshot src="/app_dash.png" alt="Análisis de rendimiento" loading="lazy" />
        </ScreenshotContainer>
        <ScreenshotContainer>
          <Screenshot src="/app_final.png" alt="Rutinas personalizadas" loading="lazy" />
        </ScreenshotContainer>
      </ScreenshotsContainer>
    </AppPromoContainer>
  </AppPromoSection>
));

// Hook personalizado para manejar scroll infinito (simulado)
const usePostsWithPagination = (initialPosts) => {
  const [posts, setPosts] = useState(initialPosts);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  
  const loadMorePosts = () => {
    if (loading || !hasMore) return;
    
    setLoading(true);
    // Simulación de carga asíncrona de más posts
    setTimeout(() => {
      // Si ya tenemos muchos posts, no cargar más
      if (posts.length >= 20) {
        setHasMore(false);
      } else {
        // Duplicamos algunos posts para simular más contenido
        setPosts(prev => [...prev, ...initialPosts.slice(0, 2)]);
      }
      setLoading(false);
    }, 800);
  };
  
  return { posts, loading, hasMore, loadMorePosts };
};

// Componente principal PostList optimizado
const PostList = ({ posts: propPosts }) => {
  const postsToUse = propPosts || defaultPosts;
  const { posts, loading, hasMore, loadMorePosts } = usePostsWithPagination(postsToUse);
  const [bottomRef, isBottomVisible] = useIntersectionObserver({
    rootMargin: '200px 0px',
  });
  
  useEffect(() => {
    if (isBottomVisible && hasMore) {
      loadMorePosts();
    }
  }, [isBottomVisible, hasMore]);
  
  return (
    <>
    <Helmet>
      <title>Blog de MyPunch</title>
      <meta name="description" content="Blog de MyPunch" />
      <link rel="canonical" href="https://blog.punchia.online" />
      <link rel="icon" href="/favicon.ico" />
      <meta property="og:title" content="Blog de MyPunch" />
      <meta property="og:description" content="Blog de MyPunch" />
      <meta property="og:image" content="/og-image.png" />
      <meta property="og:url" content="https://blog.punchia.online" />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="es_ES" />
      <meta property="og:site_name" content="Blog de MyPunch" />
    </Helmet>
      <AppPromo />
      
      <HeroCard>
        <PostCard key={`${posts[3].slug}-3`} post={posts[3]} />
      </HeroCard>

      <Newsletter />

      <Grid>
        {posts.map((post, index) => (
          <PostCard key={`${post.slug}-${index}`} post={post} />
        ))}
      </Grid>
      
      {loading && <p style={{ textAlign: 'center', padding: '2rem' }}>Cargando más artículos...</p>}
      <div ref={bottomRef} style={{ height: '10px', margin: '1rem 0' }}></div>
      
      <DonationSection>
        <DonationContainer>
          <DonationTitle>
            Apoya nuestro <span>proyecto</span>
          </DonationTitle>
          <DonationDescription>
            Tu ayuda nos permite seguir innovando en tecnologías de monitoreo inteligente para deportes de contacto.
            Juntos podemos transformar la forma en que se entrena y se compite.
          </DonationDescription>
          <DonationButton to="https://punchia.online/donations">
            Realizar donación
          </DonationButton>
        </DonationContainer>
      </DonationSection>

      <Footer />
      
    </>
  );
};

export default memo(PostList);
